'use client';

import Icon from '@ant-design/icons';
import { Button, Input, Modal, message } from 'antd';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import RightSvg from '@/components/Icon/svgs/right.svg';
import { useTranslation } from '@/i18n/client';
import { type ICircleSettingDetail, updateCircleSetting } from '@/service/friend';

import styles from './index.less';

// 阻止事件冒泡到父组件
const stopPropagationModalRender = (node: ReactNode) => (
  <div className="w-full h-full" onClick={(e) => e.stopPropagation()}>
    {node}
  </div>
);

export interface IEditDescriptionModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  circleRoute: string;
  initialDescription?: string;
  currentDetail?: ICircleSettingDetail | null;
  onSuccess?: (detail: ICircleSettingDetail) => void;
}

export const EditDescriptionModal = (props: IEditDescriptionModalProps): JSX.Element => {
  const { open, setOpen, circleRoute, initialDescription = '', currentDetail, onSuccess } = props;
  const { t } = useTranslation();

  const [editDescription, setEditDescription] = useState(initialDescription);
  const [saveLoading, setSaveLoading] = useState(false);

  // 当弹窗打开时，初始化介绍
  useEffect(() => {
    if (open) {
      setEditDescription(initialDescription);
    }
  }, [open, initialDescription]);

  // 关闭弹窗
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // 保存群介绍
  const handleSaveDescription = useCallback(() => {
    setSaveLoading(true);
    updateCircleSetting({
      route: circleRoute,
      description: editDescription.trim()
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        message.success(t('circle.updateSuccess'));
        handleClose();

        if (currentDetail) {
          onSuccess?.({
            ...currentDetail,
            description: editDescription.trim()
          });
        }
      })
      .catch((e) => {
        message.error(e.message);
      })
      .finally(() => {
        setSaveLoading(false);
      });
  }, [circleRoute, currentDetail, editDescription, handleClose, onSuccess, t]);

  return (
    <Modal
      centered
      className={styles.editDescriptionModal}
      closable={false}
      destroyOnClose
      footer={null}
      maskClosable
      modalRender={stopPropagationModalRender}
      onCancel={handleClose}
      open={open}
      width={580}
      zIndex={1100}
    >
      <div className={styles.editFormContainer}>
        {/* 头部 */}
        <div className={styles.editFormHeader}>
          <div className={styles.backBtn} onClick={handleClose}>
            <Icon className={styles.backIcon} component={RightSvg} />
          </div>
          <span className={styles.title}>{t('circle.editDescription')}</span>
          <div className={styles.placeholder} />
        </div>

        {/* 内容区 */}
        <div className={styles.editFormBody}>
          <div className={styles.inputCard}>
            <Input.TextArea
              className={styles.editDescriptionInput}
              maxLength={100}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder={t('circle.descriptionPlaceholder')}
              rows={4}
              showCount
              value={editDescription}
            />
          </div>

          {/* 保存按钮 */}
          <Button
            className={styles.saveBtn}
            disabled={saveLoading}
            loading={saveLoading}
            onClick={handleSaveDescription}
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditDescriptionModal;
